<template>
  <div>
    <ClinicPageHeader title="Pacientes" :subtitle="headerSubtitle">
      <template #actions>
        <button
          class="btn btn--outline btn--sm"
          data-test="export"
          :disabled="exporting"
          @click="exportCsv"
        >
          {{ exporting ? "Exportando…" : "Exportar" }}
        </button>
        <router-link
          :to="{ name: 'ClinicSchedule', query: { novo: '1' } }"
          class="btn btn--primary btn--sm"
        >
          + Novo agendamento
        </router-link>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar os pacientes"
        message="Ocorreu um erro ao buscar a lista de pacientes. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="fetch">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="seg" style="margin-bottom:var(--space-5)">
          <a v-for="i in 5" :key="i">
            <span
              class="skel-line"
              style="width:64px;height:14px;display:inline-block"
            />
          </a>
        </div>
        <div class="card card--flush">
          <div style="padding:var(--space-5)">
            <div
              v-for="i in 8"
              :key="i"
              class="skel-line"
              style="width:100%;height:22px;margin-bottom:16px"
            />
          </div>
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <!-- filtros (segmentado por status) -->
        <ClinicSeg
          :value="statusFilter"
          :options="statusOptions"
          style="margin-bottom:var(--space-5)"
          @input="onStatus"
        />

        <!-- toolbar -->
        <div class="dtoolbar">
          <div class="dsearch">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M21 21l-4-4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <input
              v-model="q"
              class="input"
              placeholder="Buscar por nome, CPF ou telefone…"
              @input="onSearch"
            />
          </div>
          <select v-model="doctorFilter" class="select" @change="onDoctor">
            <option value="">Todos os profissionais de saúde</option>
            <option v-for="d in doctors" :key="d.id" :value="String(d.id)">
              {{ d.name }}
            </option>
          </select>
        </div>

        <div class="card card--flush">
          <table v-if="rows.length" class="table">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Telefone</th>
                <th>Profissional de saúde responsável</th>
                <th>Última consulta</th>
                <th>Próxima consulta</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="p in rows"
                :key="p.id"
                class="tr--link"
                @click="goPatient(p)"
              >
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div class="avatar avatar--sm" :class="p.avatarClass">
                      {{ p.initials }}
                    </div>
                    <div class="stack" style="gap:0">
                      <span class="cell-strong">{{ p.name }}</span>
                      <span
                        style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                      >
                        {{ specialtyOf(p) }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="num">{{ formatPhone(p.contact) }}</td>
                <td>{{ p.lastDoctor || "—" }}</td>
                <td class="num">
                  <span v-if="p.lastAppointment">
                    {{ slotLabel(p.lastAppointment) }}
                  </span>
                  <span v-else style="color:var(--color-gray-400)">—</span>
                </td>
                <td class="num">
                  <span style="color:var(--color-gray-400)">—</span>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="`badge--${statusVariant(p.status)}`"
                  >
                    {{ p.statusLabel }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <ClinicStateBox
            v-else
            state="empty"
            title="Nenhum paciente encontrado"
            message="Ajuste os filtros ou o termo de busca para ver mais resultados."
          />

          <div
            v-if="rows.length"
            style="padding:var(--space-4) var(--space-5);border-top:1px solid var(--color-gray-100);display:flex;align-items:center;justify-content:space-between"
          >
            <span
              style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
            >
              Mostrando
              <b style="color:var(--color-gray-800)">
                {{ rangeFrom }}–{{ rangeTo }}
              </b>
              de {{ total }} pacientes
            </span>
            <div class="row" style="gap:var(--space-2)">
              <button
                class="btn btn--ghost btn--sm"
                :disabled="page <= 1"
                @click="prevPage"
              >
                ← Anterior
              </button>
              <span
                style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
              >
                Página {{ page }} de {{ pages }}
              </span>
              <button
                class="btn btn--outline btn--sm"
                :disabled="page >= pages"
                @click="nextPage"
              >
                Próxima →
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { downloadBlob } from "@/utils/downloadFile";
import { ClinicPatients, ClinicDoctors } from "@/services/clinica";
import { slotLabel } from "@/services/clinica/helpers";
import { formatPhone } from "@/utils/formatPhone";
import clinicaListQuery from "@/mixins/clinicaListQuery";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicSeg from "@/components/clinica/ClinicSeg.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";

const STATUS_VARIANTS = {
  ativo: "success",
  sem_retorno: "warning",
  novo: "info",
  inativo: "neutral"
};

export default {
  name: "ClinicPatients",
  components: { ClinicPageHeader, ClinicSeg, ClinicStateBox },
  mixins: [clinicaListQuery],
  data() {
    return {
      state: "loading",
      // Médicos reais da clínica (GET /clinica/doctors) — não fixture.
      doctors: [],
      rows: [],
      total: 0,
      page: 1,
      per: 8,
      pages: 1,
      counts: { todos: 0, ativo: 0, sem_retorno: 0, novo: 0, inativo: 0 },
      // Filtros (sincronizados com a URL via mixin clinicaListQuery).
      q: "",
      statusFilter: "todos",
      exporting: false,
      doctorFilter: ""
    };
  },
  created() {
    this.readQuery();
    this.fetch();
    this.fetchCounts();
    this.fetchDoctors();
  },
  methods: {
    formatPhone,
    slotLabel,
    async fetchDoctors() {
      try {
        const { data } = await ClinicDoctors.list();
        this.doctors = data || [];
      } catch (e) {
        this.doctors = [];
      }
    },
    /** Lê os filtros da URL para o estado local. */
    readQuery() {
      const f = this.clinicaReadQuery({});
      this.q = f.q || "";
      this.statusFilter = f.status || "todos";
      this.doctorFilter = f.doctorId || "";
      this.page = Number(f.page) || 1;
    },
    /** Grava os filtros atuais na URL (o watch de $route.query refaz o fetch). */
    syncQuery() {
      this.clinicaSyncQuery({
        q: this.q,
        status: this.statusFilter,
        doctorId: this.doctorFilter,
        page: this.page > 1 ? this.page : ""
      });
    },
    async fetch() {
      this.state = "loading";
      try {
        const { data } = await ClinicPatients.list({
          q: this.q,
          status: this.statusFilter,
          doctorId: this.doctorFilter,
          unitId: this.selectedUnitId,
          page: this.page,
          per: this.per,
          fail: this.$route.query.state === "error"
        });
        this.rows = data.items;
        this.total = data.total;
        this.page = data.page;
        this.pages = data.pages;
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    // CSV gerado pela API com a busca e o status atuais (todas as páginas).
    async exportCsv() {
      if (this.exporting) return;
      this.exporting = true;
      try {
        const { data } = await ClinicPatients.exportCsv({
          q: this.q,
          status: this.statusFilter
        });
        const now = new Date();
        const today = [
          now.getFullYear(),
          String(now.getMonth() + 1).padStart(2, "0"),
          String(now.getDate()).padStart(2, "0")
        ].join("-");
        downloadBlob(data, `pacientes_${today}.csv`);
      } catch (e) {
        this.$toast.error("Não foi possível exportar os pacientes.");
      } finally {
        this.exporting = false;
      }
    },
    async fetchCounts() {
      try {
        const { data } = await ClinicPatients.counts({
          unitId: this.selectedUnitId
        });
        this.counts = data;
      } catch (e) {
        // Contagens do segmentado não são críticas — mantém zeros.
      }
    },
    // Debounce: digitar não pode refazer o fetch a cada tecla (congela a tela).
    onSearch() {
      this.page = 1;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.syncQuery(), 300);
    },
    onStatus(value) {
      this.statusFilter = value;
      this.page = 1;
      this.syncQuery();
    },
    onDoctor() {
      this.page = 1;
      this.syncQuery();
    },
    prevPage() {
      if (this.page <= 1) return;
      this.page -= 1;
      this.syncQuery();
    },
    nextPage() {
      if (this.page >= this.pages) return;
      this.page += 1;
      this.syncQuery();
    },
    specialtyOf(patient) {
      const d = this.doctorById[patient.doctorId];
      return d ? d.specialty : "";
    },
    statusVariant(status) {
      return STATUS_VARIANTS[status] || "neutral";
    },
    goPatient(patient) {
      this.$router.push({
        name: "ClinicPatientProfile",
        params: { id: String(patient.id) }
      });
    }
  },
  computed: {
    /** Unidade ativa (contexto de clínica) — refiltra a lista ao trocar. */
    selectedUnitId() {
      return this.$store.getters["clinica/selectedUnitId"];
    },
    doctorById() {
      return this.doctors.reduce((map, d) => {
        map[d.id] = d;
        return map;
      }, {});
    },
    statusOptions() {
      return [
        { value: "ativo", label: "Ativos", count: this.counts.ativo },
        {
          value: "sem_retorno",
          label: "Sem retorno",
          count: this.counts.sem_retorno
        },
        { value: "novo", label: "Novos", count: this.counts.novo },
        { value: "inativo", label: "Inativos", count: this.counts.inativo },
        { value: "todos", label: "Todos", count: this.counts.todos }
      ];
    },
    headerSubtitle() {
      return `${this.counts.todos} cadastrados · ${this.counts.ativo} ativos`;
    },
    rangeFrom() {
      return this.total === 0 ? 0 : (this.page - 1) * this.per + 1;
    },
    rangeTo() {
      return Math.min(this.page * this.per, this.total);
    }
  },
  watch: {
    "$route.query"() {
      this.readQuery();
      this.fetch();
    },
    selectedUnitId() {
      this.page = 1;
      this.fetch();
      this.fetchCounts();
    }
  }
};
</script>
